import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useGetShops } from "../../../../hooks/useGetShops";
import { ShopClient } from "../../../../api/ShopClientsAPI";
import IconButton from "../../../../components/IconButton/IconButton";
import { Icon } from "../../../../components/Icon/Icon";
import deleteIcon from "../../../../assets/images/bin.svg";
import edit from "../../../../assets/images/pen.svg";
import { Modal } from "../../../../components/Modal/Modal";
import PartnerForm from "../../../../components/PartnerForm/PartnerForm";
import PrimaryButton from "../../../../components/PrimaryButton/PrimaryButton";
import { FormType } from "../../../../shared/form.type";
import ConfirmationPopup from "../../../../components/ConfirmationPopup/ConfirmationPopup";
import { PermissionFlags } from "../../../../components/PermissionFlags/PermissionFlags";
import Card from "../../../../components/Card/Card";
import Counter from "../../../../components/Counter/Counter";
import FilterPill from "../../../../components/FilterPill/FilterPill";
import Collapsable from "../../../../components/Collapsable/Collapsable";
import CONFIG from "../../../../config/config";
import NoDataCTA from "../../../../components/NoDataCTA/NoDataCTA";
import PageWrapper from "../../../PageWrapper/PageWrapper";
import Pill from "../../../../components/Pill/Pill";
import Table, { ColumnDefinition } from "../../../../components/Table/Table";

export type PartnerColumn = ColumnDefinition<ShopClient>;

type ShopFilter = {
  countryCode?: string;
  searchText?: string;
};

const emptyPartner: ShopClient = {
  name: '',
  supportedByApp: false,
  items: [],
  addressDataAddress: '',
  addressDataName: '',
  email: '',
  fiscalDataAddress: '',
  fiscalDataNIP: '',
  fiscalDataName: '',
  notes: '',
  phone: '',
  phoneAddress: '',
  shopId: ''
}

function PartnersSection() {

  const { t } = useTranslation();

  const [filters, setFilters] = useState<ShopFilter>({});
  const [action, setAction] = useState<FormType>('READ');
  const [partner, setPartner] = useState<ShopClient | null>();
  const { isLoadingShops, shops, addFn, updateFn, removeFn } = useGetShops(filters);
  const [uniqueCountries, setUniqueCountries] = useState<{ name: string, countryCode: string }[]>([]);

  const applyFilter = (newFilter: Partial<ShopFilter>) => {
    if (filters.countryCode === newFilter.countryCode) {
        setFilters(prevFilters => ({ ...prevFilters, countryCode: undefined }));
    } else {
        setFilters(prevFilters => ({ ...prevFilters, ...newFilter }));
    }
  };


  useEffect(() => {
    if (uniqueCountries.length === 0) {
      const countryMapping = CONFIG.COUNTRY_CODES.reduce((acc: { [key: string]: string }, country) => {
        let formattedKey = country.countryName.toLowerCase();
        formattedKey = formattedKey.replace(/\s+/g, '_');
        formattedKey = formattedKey.replace(/[^a-z_]/g, '');
        acc[country.countryCode] = formattedKey;
        return acc;
      }, {});
  
      const countries = shops.reduce<{ [key: string]: { name: string, countryCode: string } }>((acc, shop) => {
        if (shop.countryCode && !acc[shop.countryCode]) {
          acc[shop.countryCode] = {
            name: countryMapping[shop.countryCode] || shop.countryCode, // Default to countryCode if not found in the mapping
            countryCode: shop.countryCode,
          };
        }
        return acc;
      }, {});
      setUniqueCountries(Object.values(countries));
    }

  }, [shops, uniqueCountries.length]);

  const actionButton = useMemo(() => {
    if (isLoadingShops) {
      return <span className="flex justify-center"><PrimaryButton text="..." isLoading/></span>;
    }
    switch (action) {
      case 'CREATE':
        return <span className="flex justify-center"><PrimaryButton text={t("global.create")!} fn={() => registerAndClose(partner!)} /></span>;
      case 'UPDATE':
        return <span className="flex justify-center"><PrimaryButton text={t("global.update")!} fn={() => registerAndClose(partner!)} /></span>;
      default:
        return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, partner]);

  const registerAndClose = async (e: ShopClient) => {
    if (!!e) {
      if (action === 'CREATE') {
        addFn(e);
      } else {
        updateFn(e);
      }
      setPartner(null);
    }
  }

  const viewPartnerDetails = (e: ShopClient) => {
    setAction('READ');
    setPartner(e);
  }

  const showNewEmployee = () => {
    setAction('CREATE');
    setPartner(emptyPartner);
  }

  const editPartner = (e: ShopClient) => {
    setAction('UPDATE');
    setPartner(e);
  }

  const remove = async (e: ShopClient) => {
    if (!!e) {
      removeFn(e);
      setPartner(null);
    }
  }

  const partnerColumns: PartnerColumn[] = [
    {
      key: 'name',
      title: t("admin.partners.details.name") || "Name",
      render: (partner) => (
        <div className="flex items-center space-x-2">
          <p 
            className="font-medium cursor-pointer hover:text-blue-600 transition-colors"
            onClick={() => viewPartnerDetails(partner)}
          >
            {partner.name || 'Unnamed Partner'}
          </p>
          <div className="flex space-x-1">
            {partner.supportedByApp && (
              <span className="text-green-600" title={t("admin.partners.details.app_supported.tooltip") || "App Supported"}>
                📱
              </span>
            )}
            {partner.universalPartnerId ? (
              <div title={t("admin.partners.details.linked.tooltip") || "This partner is linked to you - it means you can receive or send orders on eAteliers"}>
                <Pill 
                  text={t("admin.partners.details.linked") || "Linked"}
                  color="green"
                />
              </div>
            ) : (
              <div title={t("admin.partners.details.unlinked.tooltip") || "This partner is not linked to you yet - Import its catalogues or share yours with them to connect on eAteliers and start sending and receiving orders"}>
                <Pill 
                  text={t("admin.partners.details.unlinked") || "Unlinked"}
                  color="orange"
                />
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'email',
      title: t("admin.partners.details.email") || "Email",
      render: (partner) => (
        <div className="max-w-xs">
          <span className="text-sm text-gray-600 truncate block">
            {partner.email || '-'}
          </span>
        </div>
      ),
    },
    {
      key: 'phone',
      title: t("admin.partners.details.phone") || "Phone",
      render: (partner) => (
        <div className="max-w-xs">
          <span className="text-sm text-gray-600 truncate block">
            {partner.phone || '-'}
          </span>
        </div>
      ),
    },
  ];

  const renderActions = (partner: ShopClient, index: number): React.ReactNode => {
    return (
      <div className="flex justify-center space-x-1">
        <PermissionFlags requiredFlags={['update-partners'] || []}>
          <IconButton onClick={() => editPartner(partner)}>
            <Icon url={edit} cssStyles="w-5" />
          </IconButton>
        </PermissionFlags>
        <PermissionFlags requiredFlags={['remove-partners'] || []}>
          <IconButton>
            <ConfirmationPopup 
              onConfirm={() => remove(partner)} 
              confirmText={t("global.confirm") || "Confirm"} 
              title={`${t("global.remove") || "Remove"}: ${partner.name}`}
              extraContent={() => <p><Pill text={t("global.warning") || "Warning"} color='red'/>&nbsp;{ t("admin.partners.remove.warning") || "This action cannot be undone." }</p>}
            >
              <Icon url={deleteIcon} cssStyles="w-5" />
            </ConfirmationPopup>
          </IconButton>
        </PermissionFlags>
      </div>
    );
  };

  return (
    <PageWrapper>
      <div className="space-y-5">
        <PermissionFlags requiredFlags={['see-partners'] || []}>
        <>
          <div className="container flex flex-col md:flex-row my-3 items-stretch space-x-0 md:space-x-5">
            <PermissionFlags requiredFlags={['create-partners'] || []}>
              <Card>
                <div className="container flex flex-wrap my-3 justify-between">
                    <PrimaryButton text={t("admin.partners.form.new_partner.button")!} fn={showNewEmployee}/>
                </div>
              </Card>
            </PermissionFlags>
            <Counter data={shops.length} label={t("admin.partners.count")}  />
          </div>
          { uniqueCountries.length > 0 && <Card>
            <Collapsable textOnExpanded={"admin.partners.filters.shops.hide"} textOnHiden={"admin.partners.filters.shops.expand"}>
              <div className="container flex flex-wrap max-h-48 overflow-y-scroll">
                {
                  uniqueCountries.map((country, i) => (
                    <FilterPill
                      isActive={filters.countryCode === country.countryCode}
                      key={i}
                      text={t(country.name)}
                      fn={() => applyFilter({ countryCode: country.countryCode })}
                    />
                  ))
                }
              </div>
            </Collapsable>
          </Card> }
          <Card>
            <div className="container">
              <Table<ShopClient>
                isLoading={isLoadingShops}
                data={shops}
                columns={partnerColumns}
                onRowClick={(partner) => {
                  setAction('READ');
                  setPartner(partner);
                }}
                emptyListComponent={
                  <NoDataCTA
                    headline={t("admin.partners.empty.cta.title") || "No Partners Found"}
                    subtext={t("admin.partners.empty.cta.body") || "Create your first partner to get started"}
                    primaryActionText={t("admin.partners.form.new_partner.button") || "New Partner"}
                    onPrimaryAction={showNewEmployee}
                  />
                }
                isSelectable={false}
                selectedItems={[]}
                compareFunction={(item, selectedItem) => item.id === selectedItem.id}
                renderActions={renderActions}
              />
            </div>
          </Card>
          { partner && 
            <Modal modalTitle={partner.name || "admin.partners.form.new_partner"} canClose onClose={() => setPartner(null)}>
              <PartnerForm partner={partner} onPartnerChange={e => setPartner(e)} actionButton={actionButton} isDisabled={action === 'READ'}/>
            </Modal>
          }
        </>
        </PermissionFlags>
      </div>
    </PageWrapper>
  );
}

export default PartnersSection;
